import URI from 'urijs'

/**
 * Use this module to integrate our AB experiment framework into a javascript
 * app.
 *
 * The experiment API depends on the cutlass visitor_id and the currently
 * authenticated user to determine experiment eligibility.
 */
export default class Experiment {
  /**
   * @param {string} apiUrl - The url for the REST api.
   * @param {object} options - Additional options for the instance.
   * @param {string} options.visitorId - A specific visitor id that the analytics
   *                                     framework is using.
   */
  constructor (apiUrl,
    { visitorId } = {}) {
    this.apiUrl = apiUrl
    this.visitorId = visitorId
  }

  /**
   * Get the vistor's or account's segment for an experiment
   * @param {object} options
   * @param {string} options.experiment - The name of the experiment.
   * @param {object} options.properties - Additional properties related to the context
   *                                      this experiment is calling from.
   * @param {object} options.properties.src - The src this experiment will be running from.
   * @return {string} segment - The segment for the current account or visitor depending
   *                            on the experiment type.
   */
  async segment ({ experiment, properties } = {}) {
    const response = await fetch(this.url({
      experiment,
      properties
    }), {
      method: 'GET',
      credentials: 'include'
    })
    return (await response.json())?.segment || ''
  }

  /**
   * Get the raw url to query the API for an experiment's segment
   * @param {object} options
   * @param {string} options.experiment - The name of the experiment.
   * @param {object} options.properties - Additional properties related to the context
   *                                      this experiment is calling from.
   * @param {object} options.properties.src - The src this experiment will be running from.
   * @return {string} segment - The segment for the current account or visitor depending
   *                            on the experiment type.
   */
  url ({ experiment, properties } = {}) {
    return new URI(`${this.apiUrl}/api/experiments/${experiment}`)
      .setSearch('visitor_id', this.visitorId)
      .setSearch('properties[src]', properties?.src)
  }
}
